import React from 'react';
import styled, { css } from 'styled-components';

import { EllipsisWithTooltip } from './ellipsisWithTooltip';
import { Text } from './text';
import { sizes } from '../../theme/sizes';
import { colorScheme } from '../../theme';

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  checked: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  id: string;
  maxWidth?: string;
  radio?: boolean;
  size?: keyof typeof sizes | string;
}

const CheckboxContainer = styled('div')`
  display: inline-flex;
  vertical-align: middle;
  label {
    display: flex;
    margin-bottom: 0;
  }
`;

const StyledCheckbox = styled.input.attrs({ type: 'checkbox' })<Props>`
  appearance: none;
  margin-right: 16px;
  width: ${(props) => sizes[props.size || 'lg'] || props.size};
  min-width: ${(props) => sizes[props.size || 'lg'] || props.size};
  height: ${(props) => sizes[props.size || 'lg'] || props.size};
  min-height: ${(props) => sizes[props.size || 'lg'] || props.size};
  cursor: pointer;
  background: ${(props) => (props.checked ? colorScheme.toggle.accent : colorScheme.toggle.background)};
  border-radius: 3px;
  transition: all 150ms;
  border: 1px solid ${colorScheme.toggle.border};
  border-color: ${(props) => (props.checked ? colorScheme.toggle.border : colorScheme.toggle.border)};
  position: relative;

  &:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    ~ div {
      opacity: 0.5;
    }
  }

  &:checked::after {
    content: '';
    position: absolute;
    top: calc(8% + 1px);
    left: 38%;
    width: 30%;
    height: 70%;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    display: block;
  }

  ${({ radio }) =>
    radio &&
    css`
      border-radius: 100%;

      &:checked::after {
        border-radius: 50%;
        width: 12px;
        height: 12px;
        background-color: white;
        border: none;
        transform: none;
        top: 5px;
        left: 5px;
      }
    `}
`;

export function Checkbox(props: Props) {
  const { children, ...inputProps } = props;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Toggle checkbox on Space key press
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      if (props.onChange) {
        // Create a synthetic change event
        const syntheticEvent = {
          target: event.currentTarget,
          currentTarget: event.currentTarget,
          preventDefault: () => {},
          stopPropagation: () => {},
        } as React.ChangeEvent<HTMLInputElement>;

        props.onChange(syntheticEvent);
      }
    }
  };

  return (
    <CheckboxContainer>
      <label htmlFor={props.id}>
        <StyledCheckbox {...inputProps} tabIndex={props.disabled ? -1 : 0} onKeyDown={handleKeyDown} />
        {props.maxWidth ? (
          <EllipsisWithTooltip maxWidth={props.maxWidth}>{props.label || children}</EllipsisWithTooltip>
        ) : (
          <Text>{props.label || children}</Text>
        )}
      </label>
    </CheckboxContainer>
  );
}
